<script lang="ts">
  import { onMount } from 'svelte';
  import SettingsLayout from '../../components/SettingsLayout.svelte';
  import Icon from '../../components/Icon.svelte';
  import type { IconName } from '../../lib/icons';
  import {
    fetchWebhookInfo,
    fetchWebhookLogs,
    fetchWebhookLogDetail,
    setWayforpaySecret,
    clearWayforpaySecret,
    type WebhookInfo,
    type WebhookInfoEntry,
    type WebhookLogRow,
    type WebhookLogSummary,
    type WebhookLogDetail,
  } from '../../lib/admin-api';
  import { fmtDateTime } from '../../lib/format';

  let info = $state<WebhookInfo | null>(null);
  let logs = $state<WebhookLogRow[]>([]);
  let summary = $state<WebhookLogSummary[]>([]);
  let nextCursor = $state<string | null>(null);
  let sourceFilter = $state<'' | 'wayforpay-webhook' | 'sendpulse-payment-order'>('');
  let loading = $state(true);
  let err = $state<string | null>(null);

  // Detail modal state.
  let detail = $state<WebhookLogDetail | null>(null);
  let detailLoading = $state(false);
  let detailErr = $state<string | null>(null);

  async function openDetail(id: string) {
    detail = null;
    detailErr = null;
    detailLoading = true;
    try {
      detail = await fetchWebhookLogDetail(id);
    } catch (e) {
      detailErr = e instanceof Error ? e.message : String(e);
    } finally {
      detailLoading = false;
    }
  }
  function closeDetail() {
    detail = null;
    detailErr = null;
    detailLoading = false;
  }
  function copyJson(obj: unknown) {
    void navigator.clipboard.writeText(JSON.stringify(obj, null, 2));
  }
  function copyText(s: string) {
    void navigator.clipboard.writeText(s);
  }

  /** Витягуємо `_rawHttp` з event_raw.payload (заглиблений у наш JSON-shape). */
  function rawHttpOf(payload: unknown): { contentType?: string; body?: string } | null {
    if (!payload || typeof payload !== 'object') return null;
    const r = (payload as Record<string, unknown>)._rawHttp;
    if (!r || typeof r !== 'object') return null;
    const obj = r as Record<string, unknown>;
    return {
      contentType: typeof obj.contentType === 'string' ? obj.contentType : undefined,
      body: typeof obj.body === 'string' ? obj.body : undefined,
    };
  }

  // WFP secret editor.
  let wfpSecretInput = $state('');
  let wfpSavingSecret = $state(false);
  let wfpSecretEditOpen = $state(false);
  let wfpSecretMsg = $state<string | null>(null);

  async function saveWfpSecret() {
    if (!wfpSecretInput.trim() || wfpSecretInput.length < 10) {
      wfpSecretMsg = 'Мінімум 10 символів';
      return;
    }
    wfpSavingSecret = true;
    wfpSecretMsg = null;
    try {
      await setWayforpaySecret(wfpSecretInput);
      wfpSecretInput = '';
      wfpSecretEditOpen = false;
      await load();
      wfpSecretMsg = 'Збережено';
      setTimeout(() => (wfpSecretMsg = null), 3000);
    } catch (e) {
      wfpSecretMsg = e instanceof Error ? e.message : String(e);
    } finally {
      wfpSavingSecret = false;
    }
  }
  async function clearWfpSecret() {
    if (!confirm('Видалити збережений WayForPay merchant secret?')) return;
    try {
      await clearWayforpaySecret();
      await load();
    } catch (e) {
      wfpSecretMsg = e instanceof Error ? e.message : String(e);
    }
  }

  async function load() {
    loading = true;
    err = null;
    try {
      const [i, l] = await Promise.all([
        fetchWebhookInfo(),
        fetchWebhookLogs({
          source: sourceFilter || undefined,
          limit: 50,
        }),
      ]);
      info = i;
      logs = l.items;
      nextCursor = l.nextCursor;
      summary = l.summary;
    } catch (e) {
      err = e instanceof Error ? e.message : String(e);
    } finally {
      loading = false;
    }
  }
  onMount(load);

  async function loadMore() {
    if (!nextCursor) return;
    const r = await fetchWebhookLogs({
      source: sourceFilter || undefined,
      limit: 50,
      cursor: nextCursor,
    });
    logs = [...logs, ...r.items];
    nextCursor = r.nextCursor;
  }

  function copyToClipboard(text: string) {
    void navigator.clipboard.writeText(text);
  }

  function actionParts(action: string): { source: string; event: string } {
    // Формат: 'webhook.<source>.<event>'
    const m = action.match(/^webhook\.([^.]+)\.(.+)$/);
    if (!m) return { source: '?', event: action };
    return { source: m[1]!, event: m[2]! };
  }

  function eventClass(event: string): string {
    if (event === 'replay') return 'text-[var(--color-text-muted)] bg-[var(--color-bg-3)]';
    if (event === 'rejected') return 'text-red-700 bg-red-50';
    return 'text-[var(--color-pu-3)] bg-purple-50';
  }

  function eventLabel(event: string): string {
    if (event === 'replay') return 'replay (dup)';
    if (event === 'rejected') return 'rejected';
    return event;
  }

  /**
   * Для payment_created / skipped — показуємо реальний WFP transactionStatus
   * (Approved / Expired / Declined / Refunded / ...) кольором за статусом.
   */
  function statusChip(meta: Record<string, unknown> | null | undefined): { label: string; cls: string } {
    const rs = String((meta as { rawStatus?: unknown })?.rawStatus ?? (meta as { status?: unknown })?.status ?? '—');
    switch (rs) {
      case 'Approved':
      case 'SUCCEEDED':
        return { label: rs, cls: 'text-emerald-700 bg-emerald-50' };
      case 'Refunded':
      case 'Reversed':
      case 'Voided':
      case 'REFUNDED':
        return { label: rs, cls: 'text-blue-700 bg-blue-50' };
      case 'Declined':
      case 'Failed':
      case 'FAILED':
        return { label: rs, cls: 'text-red-700 bg-red-50' };
      case 'Expired':
        return { label: rs, cls: 'text-amber-700 bg-amber-50' };
      case 'InProcessing':
      case 'Pending':
      case 'WaitingAuthComplete':
      case 'PENDING':
        return { label: rs, cls: 'text-gray-600 bg-gray-100' };
      default:
        return { label: rs, cls: 'text-gray-700 bg-gray-100' };
    }
  }
  function isPaymentEvent(event: string): boolean {
    return event === 'payment_created' || event === 'skipped';
  }

  function sourceLabel(source: string): string {
    if (source === 'wayforpay-webhook') return 'WayForPay';
    if (source === 'sendpulse-payment-order') return 'SendPulse';
    return source;
  }
</script>

<SettingsLayout active="webhooks">
  <div class="mx-auto max-w-[1200px]">
    <header class="mb-5">
      <h1 class="text-xl font-semibold leading-tight">Webhooks</h1>
      <p class="mt-1 text-[12px] text-[var(--color-text-muted)]">
        Endpoint'и куди WayForPay / SendPulse надсилають платежі · конфігурація
        та лог активацій
      </p>
    </header>

    {#if loading}
      <div class="py-20 text-center text-sm text-[var(--color-text-muted)]">Завантаження…</div>
    {:else if err}
      <div class="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">{err}</div>
    {:else if info}
      <!-- Two webhook cards side by side -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        {#each [
          { key: 'wayforpay', icon: 'payments', title: 'WayForPay (прямий)', entry: info.wayforpay },
          { key: 'sendpulse', icon: 'email', title: 'SendPulse (payment_order)', entry: info.sendpulse },
        ] as card (card.key)}
          {@const entry = card.entry as WebhookInfoEntry}
          <div class="rounded-xl border border-[var(--color-border-3)] bg-white p-4">
            <div class="inline-flex items-center gap-1.5 text-sm font-bold mb-3"><Icon name={card.icon as IconName} size={15} /> {card.title}</div>

            <div class="space-y-2.5 text-xs">
              <div>
                <div class="text-[10px] uppercase tracking-[1px] text-[var(--color-text-muted-2)] mb-1">URL</div>
                <div class="flex items-center gap-2 rounded bg-[var(--color-bg-3)] px-2.5 py-1.5 font-mono text-[11px]">
                  <span class="flex-1 truncate">{entry.url}</span>
                  <button
                    onclick={() => copyToClipboard(entry.url)}
                    class="text-[10px] text-[var(--color-pu-3)] hover:underline"
                    title="Копіювати"
                  >копіювати</button>
                </div>
              </div>

              <div class="grid grid-cols-2 gap-2 text-[10.5px]">
                <div>
                  <span class="text-[var(--color-text-muted-2)]">Метод:</span>
                  <code class="ml-1 font-mono">{entry.method}</code>
                </div>
                <div>
                  <span class="text-[var(--color-text-muted-2)]">Content-Type:</span>
                  <code class="ml-1 font-mono">{entry.contentType}</code>
                </div>
              </div>

              {#if entry.responseFormat}
                <div>
                  <div class="text-[10px] uppercase tracking-[1px] text-[var(--color-text-muted-2)] mb-1">Формат відповіді</div>
                  <div class="font-mono text-[10.5px] break-all text-[var(--color-text-muted)]">{entry.responseFormat}</div>
                </div>
              {/if}

              {#if entry.eventType}
                <div>
                  <div class="text-[10px] uppercase tracking-[1px] text-[var(--color-text-muted-2)] mb-1">Тип події</div>
                  <code class="font-mono text-[11px]">{entry.eventType}</code>
                </div>
              {/if}

              <div class="border-t border-[var(--color-border-3)] pt-2 text-[10.5px] text-[var(--color-text-muted)]">
                {entry.instructions}
              </div>

              {#if card.key === 'wayforpay'}
                <div class="border-t border-[var(--color-border-3)] pt-3">
                  <div class="flex items-center justify-between mb-2">
                    <div class="text-[10px] uppercase tracking-[1px] font-semibold text-[var(--color-text-muted-2)]">
                      Merchant secret
                    </div>
                    {#if entry.secretConfigured}
                      <span class="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold text-emerald-700">
                        ● встановлено{entry.secretSource === 'env' ? ' (env)' : ''}
                      </span>
                    {:else}
                      <span class="rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-bold text-red-700">
                        ○ не встановлено
                      </span>
                    {/if}
                  </div>

                  {#if !wfpSecretEditOpen}
                    <div class="flex items-center gap-2">
                      <button
                        onclick={() => { wfpSecretEditOpen = true; wfpSecretInput = ''; wfpSecretMsg = null; }}
                        class="rounded-md border border-[var(--color-border-3)] bg-white px-3 py-1.5 text-[11px] font-semibold hover:bg-[var(--color-bg-3)]"
                      >
                        {entry.secretConfigured ? 'Змінити' : 'Ввести секрет'}
                      </button>
                      {#if entry.secretConfigured && entry.secretSource === 'db'}
                        <button
                          onclick={clearWfpSecret}
                          class="text-[10.5px] text-red-600 hover:underline"
                        >Очистити</button>
                      {/if}
                      {#if wfpSecretMsg}
                        <span class="text-[10.5px] text-emerald-700">{wfpSecretMsg}</span>
                      {/if}
                    </div>
                  {:else}
                    <div class="space-y-2">
                      <input
                        bind:value={wfpSecretInput}
                        type="password"
                        placeholder="Secret Key з кабінету WayForPay (≥10 символів)"
                        autocomplete="off"
                        class="w-full rounded-md border border-[var(--color-border-3)] bg-white px-2.5 py-1.5 text-[11px] font-mono"
                      />
                      <div class="flex items-center gap-2">
                        <button
                          onclick={saveWfpSecret}
                          disabled={wfpSavingSecret}
                          class="rounded-md bg-[var(--color-pu)] px-3 py-1.5 text-[11px] font-semibold text-white disabled:opacity-50"
                        >{wfpSavingSecret ? 'Зберігаю…' : 'Зберегти'}</button>
                        <button
                          onclick={() => { wfpSecretEditOpen = false; wfpSecretInput = ''; wfpSecretMsg = null; }}
                          class="rounded-md border border-[var(--color-border-3)] px-3 py-1.5 text-[11px] font-medium"
                        >Скасувати</button>
                        {#if wfpSecretMsg}
                          <span class="text-[10.5px] text-red-700">{wfpSecretMsg}</span>
                        {/if}
                      </div>
                    </div>
                  {/if}
                </div>
              {/if}
            </div>
          </div>
        {/each}
      </div>

      <!-- Summary -->
      {#if summary.length > 0}
        <div class="mb-3 flex flex-wrap gap-2">
          {#each summary as s (s.action)}
            {@const parts = actionParts(s.action)}
            <div class="rounded-md border border-[var(--color-border-3)] bg-white px-2.5 py-1 text-[11px]">
              <span class="text-[var(--color-text-muted)]">{sourceLabel(parts.source)} · {eventLabel(parts.event)}</span>
              <strong class="ml-1.5 tabular-nums">{s.count}</strong>
            </div>
          {/each}
        </div>
      {/if}

      <!-- Activation log -->
      <div class="rounded-xl border border-[var(--color-border-3)] bg-white overflow-hidden">
        <div class="flex items-center justify-between border-b border-[var(--color-border-3)] bg-[var(--color-bg-3)] px-3.5 py-2">
          <div class="text-[13px] font-bold">Лог активацій</div>
          <select
            bind:value={sourceFilter}
            onchange={load}
            class="rounded-md border border-[var(--color-border-3)] bg-white px-2.5 py-1 text-xs font-medium"
          >
            <option value="">Усі джерела</option>
            <option value="wayforpay-webhook">WayForPay</option>
            <option value="sendpulse-payment-order">SendPulse</option>
          </select>
        </div>

        {#if logs.length === 0}
          <div class="py-12 text-center text-xs text-[var(--color-text-muted)]">
            Поки немає активацій. Як тільки прийде webhook — з'явиться тут.
          </div>
        {:else}
          <table class="w-full text-xs">
            <thead class="bg-[var(--color-bg-3)]">
              <tr class="text-[10px] uppercase tracking-[1px] text-[var(--color-text-muted-2)]">
                <th class="text-left px-3 py-2 font-bold">Час</th>
                <th class="text-left px-3 py-2 font-bold">Джерело</th>
                <th class="text-left px-3 py-2 font-bold">Подія</th>
                <th class="text-left px-3 py-2 font-bold">Order Ref</th>
                <th class="text-left px-3 py-2 font-bold">Деталі</th>
              </tr>
            </thead>
            <tbody>
              {#each logs as r (r.id)}
                {@const parts = actionParts(r.action)}
                <tr
                  class="border-t border-[var(--color-border-3)] hover:bg-[var(--color-bg-3)] cursor-pointer"
                  onclick={() => openDetail(r.id)}
                  title="Натисни щоб подивитись усі дані"
                >
                  <td class="px-3 py-2 text-[11px] tabular-nums whitespace-nowrap">{fmtDateTime(r.occurredAt)}</td>
                  <td class="px-3 py-2 text-[11px] font-semibold">{sourceLabel(parts.source)}</td>
                  <td class="px-3 py-2">
                    {#if isPaymentEvent(parts.event)}
                      {@const sc = statusChip(r.meta)}
                      <span class="inline-block rounded-full px-1.5 py-0.5 text-[10px] font-bold {sc.cls}">
                        {sc.label}
                      </span>
                      {#if parts.event === 'skipped'}
                        <span class="ml-1 text-[9.5px] text-[var(--color-text-muted-2)] uppercase tracking-wider">пропущено</span>
                      {/if}
                    {:else}
                      <span class="inline-block rounded-full px-1.5 py-0.5 text-[10px] font-bold {eventClass(parts.event)}">
                        {eventLabel(parts.event)}
                      </span>
                    {/if}
                  </td>
                  <td class="px-3 py-2 font-mono text-[10.5px] text-[var(--color-text-muted)]">
                    {r.meta?.externalRef ?? r.targetId ?? '—'}
                  </td>
                  <td class="px-3 py-2 text-[10.5px] text-[var(--color-text-muted)]">
                    {#if r.meta?.amountCents}
                      <span class="tabular-nums">{Number(r.meta.amountCents) / 100} {r.meta?.currency ?? 'UAH'}</span>
                    {/if}
                    {#if r.meta?.reason}· {r.meta.reason}{/if}
                  </td>
                </tr>
              {/each}
            </tbody>
          </table>
          {#if nextCursor}
            <div class="border-t border-[var(--color-border-3)] bg-[var(--color-bg-3)] px-3.5 py-2 text-center">
              <button
                onclick={loadMore}
                class="rounded-md border border-[var(--color-border-3)] bg-white px-3 py-1.5 text-xs font-semibold hover:bg-white"
              >Завантажити ще</button>
            </div>
          {/if}
        {/if}
      </div>
    {/if}
  </div>

  <!-- Detail drawer/modal -->
  {#if detailLoading || detail || detailErr}
    <div
      class="fixed inset-0 z-40 bg-black/40 flex items-start justify-center pt-12 pb-8 overflow-y-auto"
      onclick={closeDetail}
      role="presentation"
    >
      <div
        class="relative w-[min(960px,95vw)] rounded-xl bg-white shadow-2xl"
        onclick={(e) => e.stopPropagation()}
        role="dialog"
      >
        <div class="flex items-center justify-between border-b border-[var(--color-border-3)] px-5 py-3">
          <div class="text-sm font-bold">Деталі webhook-активації</div>
          <button onclick={closeDetail} class="text-xl leading-none text-[var(--color-text-muted)] hover:text-[var(--color-text)]" aria-label="Закрити">×</button>
        </div>

        <div class="p-5">
          {#if detailLoading}
            <div class="py-12 text-center text-sm text-[var(--color-text-muted)]">Завантаження…</div>
          {:else if detailErr}
            <div class="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">{detailErr}</div>
          {:else if detail?.log}
            <!-- 1. Audit log -->
            <section class="mb-5">
              <div class="text-[10px] uppercase tracking-[1px] text-[var(--color-text-muted-2)] font-bold mb-1.5">
                Audit log entry
              </div>
              <div class="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                <div><span class="text-[var(--color-text-muted)]">Час:</span> {fmtDateTime(detail.log.occurredAt)}</div>
                <div><span class="text-[var(--color-text-muted)]">Action:</span> <code class="font-mono text-[11px]">{detail.log.action}</code></div>
                <div><span class="text-[var(--color-text-muted)]">Target type:</span> {detail.log.targetType ?? '—'}</div>
                <div><span class="text-[var(--color-text-muted)]">Target id:</span> <code class="font-mono text-[10.5px]">{detail.log.targetId ?? '—'}</code></div>
                <div><span class="text-[var(--color-text-muted)]">Actor:</span> {detail.log.actorId ?? 'system'}</div>
                <div><span class="text-[var(--color-text-muted)]">IP:</span> {detail.log.ip ?? '—'}</div>
              </div>
              {#if detail.log.meta && Object.keys(detail.log.meta).length > 0}
                <div class="mt-2">
                  <div class="flex items-center justify-between mb-1">
                    <div class="text-[10px] uppercase tracking-[1px] text-[var(--color-text-muted-2)] font-semibold">meta</div>
                    <button onclick={() => copyJson(detail!.log!.meta)} class="text-[10px] text-[var(--color-pu-3)] hover:underline">копіювати JSON</button>
                  </div>
                  <pre class="rounded bg-[var(--color-bg-3)] p-2.5 text-[10.5px] font-mono whitespace-pre-wrap break-all max-h-[200px] overflow-y-auto">{JSON.stringify(detail.log.meta, null, 2)}</pre>
                </div>
              {/if}
            </section>

            <!-- 2. Payment row (якщо є) -->
            {#if detail.payment}
              <section class="mb-5 rounded-xl border border-[var(--color-border-3)] bg-[var(--color-bg-3)] p-3.5">
                <div class="flex items-center justify-between mb-2">
                  <div class="text-[10px] uppercase tracking-[1px] text-[var(--color-text-muted-2)] font-bold">
                    Payment row
                  </div>
                  <a href="#/payments/{detail.payment.id}" class="text-[10.5px] text-[var(--color-pu-3)] hover:underline">відкрити сторінку →</a>
                </div>
                <div class="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                  <div><span class="text-[var(--color-text-muted)]">External ref:</span> <code class="font-mono text-[10.5px]">{detail.payment.externalRef ?? '—'}</code></div>
                  <div><span class="text-[var(--color-text-muted)]">Час:</span> {fmtDateTime(detail.payment.occurredAt)}</div>
                  <div><span class="text-[var(--color-text-muted)]">Сума:</span> <strong>{detail.payment.amountCents / 100} {detail.payment.currency}</strong></div>
                  <div><span class="text-[var(--color-text-muted)]">Status:</span> <strong>{detail.payment.status}</strong> ({detail.payment.rawStatus})</div>
                  <div><span class="text-[var(--color-text-muted)]">Метод:</span> {detail.payment.paymentMethod ?? '—'}</div>
                  <div><span class="text-[var(--color-text-muted)]">Картка:</span> {detail.payment.cardBrand ?? '—'} {detail.payment.cardLast4 ? `••${detail.payment.cardLast4}` : ''}</div>
                  <div><span class="text-[var(--color-text-muted)]">Fee/Net:</span> {detail.payment.feeCents != null ? `${detail.payment.feeCents / 100} / ${(detail.payment.netCents ?? 0) / 100}` : '—'}</div>
                  <div><span class="text-[var(--color-text-muted)]">Tariff:</span> {detail.payment.rawTariff ?? '—'}</div>
                  <div><span class="text-[var(--color-text-muted)]">Email:</span> {detail.payment.payerEmail ?? '—'}</div>
                  <div><span class="text-[var(--color-text-muted)]">Phone:</span> {detail.payment.payerPhone ?? '—'}</div>
                  <div class="col-span-2"><span class="text-[var(--color-text-muted)]">Ім'я платника:</span> {[detail.payment.payerFirstName, detail.payment.payerLastName].filter(Boolean).join(' ') || '—'}</div>
                </div>
                {#if detail.payment.person}
                  <div class="mt-2 pt-2 border-t border-[var(--color-border-3)] text-xs">
                    <span class="text-[var(--color-text-muted)]">Person:</span>
                    <a href="#/persons/{detail.payment.person.id}" class="ml-1 text-[var(--color-pu-3)] hover:underline font-semibold">
                      {detail.payment.person.displayName ?? '— без імені —'}
                    </a>
                    {#if detail.payment.person.username}· @{detail.payment.person.username}{/if}
                    {#if detail.payment.person.primaryEmail}· {detail.payment.person.primaryEmail}{/if}
                  </div>
                {/if}
              </section>
            {/if}

            <!-- 3. event_raw payloads -->
            {#if detail.eventRaw.length > 0}
              <section>
                <div class="text-[10px] uppercase tracking-[1px] text-[var(--color-text-muted-2)] font-bold mb-1.5">
                  Записи event_raw ({detail.eventRaw.length})
                </div>
                {#each detail.eventRaw as ev (ev.id)}
                  {@const raw = rawHttpOf(ev.payload)}
                  <details class="mb-2 rounded-lg border border-[var(--color-border-3)] bg-white overflow-hidden" open>
                    <summary class="cursor-pointer px-3 py-2 text-xs flex items-center gap-2 hover:bg-[var(--color-bg-3)]">
                      <code class="font-mono text-[11px]">{ev.type}</code>
                      <span class="text-[10.5px] text-[var(--color-text-muted)]">· {fmtDateTime(ev.receivedAt)}</span>
                      {#if ev.processed}
                        <span class="ml-auto rounded-full bg-emerald-100 px-1.5 py-0.5 text-[9.5px] font-bold text-emerald-700">processed</span>
                      {:else}
                        <span class="ml-auto rounded-full bg-amber-100 px-1.5 py-0.5 text-[9.5px] font-bold text-amber-700">pending</span>
                      {/if}
                    </summary>
                    <div class="px-3 pb-3 space-y-3">
                      {#if ev.processingError}
                        <div class="flex items-start gap-1.5 rounded bg-red-50 border border-red-200 px-2 py-1 text-[10.5px] text-red-700">
                          <Icon name="statusFail" size={13} class="shrink-0 mt-0.5" /> {ev.processingError}
                        </div>
                      {/if}

                      <!-- Сирий HTTP body — як прийшов від acquirer -->
                      {#if raw && (raw.body || raw.contentType)}
                        <div>
                          <div class="flex items-center justify-between mb-1">
                            <div class="text-[10px] uppercase tracking-[1px] text-[var(--color-text-muted-2)] font-semibold">
                              Сирий HTTP (як отримали)
                            </div>
                            {#if raw.body}
                              <button onclick={() => copyText(raw.body ?? '')} class="text-[10px] text-[var(--color-pu-3)] hover:underline">копіювати raw</button>
                            {/if}
                          </div>
                          {#if raw.contentType}
                            <div class="mb-1 text-[10.5px] text-[var(--color-text-muted)]">
                              <span>Content-Type:</span> <code class="font-mono">{raw.contentType}</code>
                            </div>
                          {/if}
                          {#if raw.body}
                            <pre class="rounded bg-amber-50 border border-amber-200 p-2.5 text-[10.5px] font-mono whitespace-pre-wrap break-all max-h-[300px] overflow-y-auto">{raw.body}</pre>
                          {:else}
                            <div class="text-[10.5px] italic text-[var(--color-text-muted)]">тіло порожнє</div>
                          {/if}
                        </div>
                      {/if}

                      <!-- Нормалізований payload (наш JSON-shape) -->
                      <div>
                        <div class="flex items-center justify-between mb-1">
                          <div class="text-[10px] uppercase tracking-[1px] text-[var(--color-text-muted-2)] font-semibold">
                            Нормалізований payload
                          </div>
                          <button onclick={() => copyJson(ev.payload)} class="text-[10px] text-[var(--color-pu-3)] hover:underline">копіювати JSON</button>
                        </div>
                        <pre class="rounded bg-[var(--color-bg-3)] p-2.5 text-[10.5px] font-mono whitespace-pre-wrap break-all max-h-[400px] overflow-y-auto">{JSON.stringify(ev.payload, null, 2)}</pre>
                      </div>
                    </div>
                  </details>
                {/each}
              </section>
            {:else}
              <section>
                <div class="text-[10.5px] italic text-[var(--color-text-muted)]">
                  Сирого event_raw payload-у не знайдено (можливо подія типу "rejected" не зберігається у event_raw).
                </div>
              </section>
            {/if}
          {:else}
            <div class="py-8 text-center text-sm text-[var(--color-text-muted)]">Запис не знайдено</div>
          {/if}
        </div>
      </div>
    </div>
  {/if}
</SettingsLayout>
